/**
 * Docs category labels, the slugs derived from them, and which docs space
 * a page belongs to.
 *
 * Plain ESM with no build step of its own, for the same reason as its
 * siblings `docAccessRules.js` and `docSlug.js`:
 * `scripts/build-docs-search-index.mjs` runs under plain `node`, and it
 * needs the *same* slug function the browser uses. A slug computed one
 * way at build time and another way at render time is a category link
 * that lands on nothing — the identical silent failure `docSlug.js` was
 * extracted to prevent for heading anchors.
 *
 * ## The label carries an emoji; the slug never does
 *
 * Categories are written as `'🔌 Meter data & alerts'`. The emoji is
 * decoration:
 *
 *   - It is **not** part of the slug, so re-illustrating a category does
 *     not break its URL.
 *   - It is separable via `splitCategoryLabel`, so a renderer can mark it
 *     `aria-hidden` and leave the text as the accessible name. An emoji
 *     read aloud in full ("electric plug") before every category name is
 *     noise.
 *
 * Two labels differing *only* by emoji slug identically. That is a
 * collision, not a feature, and `--check` fails on it rather than letting
 * one category silently absorb another.
 *
 * ## Spaces are derived from the audience, not from the directory
 *
 * This is where this project diverges from the reference implementation
 * it was ported from, and the divergence is the whole design.
 *
 * In the reference, `docs/public/`, `docs/users/` and `docs/admin/` each
 * *were* an audience tier, enforced by URL prefix, so a page's space fell
 * straight out of its path. Here the directory tree says almost nothing
 * about readership: `resolveDocAudience` decides it from three rules in
 * precedence order (exact-path rulings, the `-technical` basename
 * convention, then directory prefixes), and `docs/power/` alone splits
 * five ways across five audiences. Deriving a space from the path would
 * therefore put the member-facing `power/user-guide` and the
 * site-admin-only `power/finance-technical` in the same space.
 *
 * So the space is derived from the resolved audience instead. Same
 * principle as the reference — derive, never declare — applied to the
 * thing that actually carries the meaning here.
 *
 * A wanted consequence: correcting a page's audience moves it between
 * spaces with no second edit. Several `docs/power/` and `docs/support/`
 * pages are written for members but currently resolve to `site_admin`
 * through a directory default; when that is fixed they relocate on their
 * own.
 */

/**
 * Leading emoji, including variation selectors, zero-width joiners and
 * skin-tone modifiers, so a multi-codepoint glyph is taken whole rather
 * than split down the middle.
 */
const LEADING_EMOJI =
    /^((?:\p{Extended_Pictographic}|️|‍|[\u{1F3FB}-\u{1F3FF}]|\p{Emoji_Modifier_Base})+)\s*([\s\S]*)$/u

/**
 * Split `'🔌 Meter data & alerts'` into
 * `{ emoji: '🔌', text: 'Meter data & alerts' }`.
 *
 * A label with no emoji returns an empty `emoji` and its text unchanged,
 * so emoji stay optional — nothing here forces one.
 */
export function splitCategoryLabel(label) {
    const raw = String(label ?? '').trim()
    const match = raw.match(LEADING_EMOJI)
    if (!match) return { emoji: '', text: raw }

    const [, emoji, rest] = match
    const text = rest.trim()

    // An all-emoji label leaves nothing to slug. Return it as text rather
    // than as decoration, so `categorySlug` produces '' and the build
    // script's non-empty assertion reports it — better a loud build
    // failure than a category whose URL is `/docs/category/`.
    if (!text) return { emoji: '', text: raw }

    return { emoji, text }
}

/**
 * URL slug for a category label. Emoji-free, lowercase, hyphenated.
 *
 *   '🔌 Meter data & alerts'  -> 'meter-data-alerts'
 *   'Costs & budgets'         -> 'costs-budgets'
 *   "Your building's meters"  -> 'your-buildings-meters'
 *
 * Returns `''` for a label with no sluggable text; callers validate.
 */
export function categorySlug(label) {
    const { text } = splitCategoryLabel(label)
    return (
        text
            .normalize('NFKD')
            // Strip combining marks so 'Café' and 'Cafe' agree rather than
            // producing two categories that look identical in the nav.
            .replace(/[̀-ͯ]/g, '')
            .toLowerCase()
            // Apostrophes are *removed*, not hyphenated, so "building's"
            // slugs to "buildings" rather than "building-s". Both straight
            // and typographic forms, since MDX prose uses the curly one.
            .replace(/['’]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
    )
}

/**
 * Group labels that collide on slug.
 *
 * Returns `{ slug: [label, ...] }` for every slug claimed by more than
 * one distinct label. An empty object means every category is
 * addressable.
 */
export function categorySlugCollisions(labels) {
    const bySlug = new Map()
    for (const label of new Set(labels)) {
        const slug = categorySlug(label)
        if (!bySlug.has(slug)) bySlug.set(slug, [])
        bySlug.get(slug).push(label)
    }

    const collisions = {}
    for (const [slug, claimants] of bySlug) {
        if (claimants.length > 1) collisions[slug] = claimants.sort()
    }
    return collisions
}

/**
 * The three docs spaces, in the order they are presented.
 *
 * `guides` and `operations` are genuinely different readerships. The
 * partner console is a third because its readers are reseller staff who
 * are neither: a partner admin administers other organisations' seats and
 * branding, and has no interest in either a school's own dashboards or
 * the platform's deployment topology.
 */
export const DOC_SPACES = ['guides', 'partner', 'operations']

/** Human label per space, used by the landing page and the menu. */
export const SPACE_LABELS = {
    guides: 'Using Power',
    partner: 'Partner console',
    operations: 'Operations & engineering'
}

/** One sentence per space, for the landing page's section headers. */
export const SPACE_BLURBS = {
    guides: 'How to read the dashboards and run your organisation day to day.',
    partner:
        'Managing a portfolio of organisations through the partner console.',
    operations:
        'How the platform is built, deployed and operated. Site admins only.'
}

/**
 * Which space an audience's pages belong to.
 *
 * An unmapped audience falls to `operations`, matching
 * `resolveDocAudience`'s own refuse-rather-than-admit default: a new
 * audience nobody has classified should surface to the *narrowest*
 * readership, never the widest.
 */
const SPACE_BY_AUDIENCE = {
    member: 'guides',
    leadership: 'guides',
    org_admin: 'guides',
    any_admin: 'guides',
    partner: 'partner',
    site_admin: 'operations'
}

/** The docs space for a resolved audience. */
export function spaceForAudience(audience) {
    return SPACE_BY_AUDIENCE[audience] ?? 'operations'
}
